import type { ReactNode } from 'react';
import { cls } from '@/shared/lib/classes';
import { descreverErro } from '@/shared/lib/api';
import { useDica } from '@/shared/ui/Dica';

/**
 * Aviso — a faixa de mensagem do sistema.
 *
 * Barra vertical grossa à esquerda em vez de caixa colorida inteira: mantém o
 * texto sobre papel, que é onde ele é mais legível sob sol, e ainda assim
 * identifica o tipo da mensagem à distância.
 *
 * ## O raciocínio pode recolher na marca `?`; a declaração, não
 *
 * Os avisos de recorte dos painéis chegaram a dois parágrafos de seis linhas —
 * mais altura do que a fila de cartões que eles qualificam, paga em toda visita
 * por quem já os leu. Mas eles não são dica de leitura: são a declaração de que
 * a tela está descartando linha, e recolhê-los **inteiros** transformaria um
 * corte declarado no corte silencioso que a `.spec/002` chama de defeito.
 *
 * A `dica` resolve os dois lados. O que fica no corpo é a frase que declara o
 * corte — "só entra o dinheiro que tem obra" —, e ela continua na faixa
 * colorida, visível sem clique. O que vai para a marca é o **porquê**: de onde
 * vem a regra, o que fica de fora, por que os totais não batem com os da outra
 * tela. Quem lê a primeira vez abre; quem já sabe paga uma linha em vez de oito.
 *
 * A marca fica na linha do `titulo`, como no `SecaoFormulario` — e por isso
 * `dica` **exige** `titulo`: sem ele não há onde pendurá-la sem competir com a
 * frase do corpo, que é justamente a que precisa ser lida.
 */

export type TomAviso = 'erro' | 'atencao' | 'info';

const TONS: Record<TomAviso, string> = {
  erro: 'border-l-perigo-500 bg-perigo-50 text-perigo-700',
  atencao: 'border-l-zarcao-500 bg-zarcao-50 text-zarcao-700',
  info: 'border-l-grafite-600 bg-papel-200 text-grafite-800',
};

export interface AvisoProps {
  tom?: TomAviso;
  titulo?: string;
  /**
   * O raciocínio por trás do aviso, recolhido na marca `?` ao lado do título.
   *
   * Só o **porquê** entra aqui. A frase que declara o que a tela está fazendo
   * fica em `children`, na faixa colorida — ver o cabeçalho deste arquivo.
   *
   * Ignorada sem `titulo`, que é onde a marca mora.
   */
  dica?: ReactNode;
  children: ReactNode;
  className?: string;
}

export function Aviso({
  tom = 'info',
  titulo,
  dica,
  children,
  className,
}: AvisoProps): React.ReactElement {
  const { idDica, marca, nota } = useDica({
    dica: titulo ? dica : undefined,
    rotulo: titulo ?? '',
    alvo: 'aviso',
  });

  return (
    <div
      role={tom === 'erro' ? 'alert' : 'status'}
      className={cls(
        'border border-l-4 border-transparent px-4 py-3 text-sm',
        TONS[tom],
        className,
      )}
    >
      {titulo ? (
        <div className="mb-1 flex items-center gap-2">
          <p className="cota">{titulo}</p>
          {marca}
        </div>
      ) : null}

      <div aria-describedby={idDica}>{children}</div>

      {/*
        A nota fecha o aviso, depois da frase que declara o corte: é a ordem em
        que se lê — primeiro o que a tela faz, depois por quê. Fechada é
        `sr-only` e sai do fluxo, e por isso não há `<div>` com margem em volta
        dela cobrando altura de quem nunca a abre.
      */}
      {nota}
    </div>
  );
}

/**
 * Aviso a partir de um erro da API, **dentro** do fluxo da tela.
 *
 * Para o resultado de uma gravação, use a `FaixaStatus`: ela aparece no topo,
 * com fundo sólido, e não passa despercebida. Este componente é para erro de
 * contexto — uma consulta secundária que falhou, um bloco que não carregou.
 */
export function AvisoErro({
  erro,
  className,
}: {
  erro: unknown;
  className?: string;
}): React.ReactElement | null {
  if (!erro) return null;

  const { texto, requestId } = descreverErro(erro);

  return (
    <Aviso tom="erro" className={className}>
      <p>{texto}</p>
      {requestId ? (
        <p className="mt-1 font-mono text-[11px] opacity-70">código: {requestId}</p>
      ) : null}
    </Aviso>
  );
}
